import { cleanDocument, clearMask, formatTextDate, splitFullName } from "@/utils";

export enum ShareholderKind {
    NATURAL = "NATURAL",
    LEGAL = "LEGAL",
}

export interface AddressFormInput {
    street: string;
    number: string;
    complement?: string;
    neighborhood: string;
    zipCode: string;
    city: string;
    state: string;
}

export interface BankingAddress {
    street: string;
    number: string;
    complement?: string;
    neighborhood: string;
    postalCode: string;
    city: string;
    state: string;
    country: string;
}

export interface BankingShareholder {
    document: string;
    type: string;
    email: string;
    phoneNumber: string;
    address: BankingAddress;
    firstName?: string;
    lastName?: string;
    birthDate?: string;
    monthlyRevenueRange?: string;
    legalName?: string;
    annualRevenueRange?: string;
}

export const toBrazilianPhone = (value?: string) => {
    const digits = (value ?? "").replace(/\D/g, "");
    if (!digits) return "";
    if (digits.startsWith("55") && (digits.length === 12 || digits.length === 13)) {
        return digits;
    }
    return `55${digits}`;
};

export const buildAddress = (raw: AddressFormInput): BankingAddress => ({
    street: raw.street,
    number: raw.number,
    complement: raw.complement || undefined,
    neighborhood: raw.neighborhood,
    postalCode: clearMask(raw.zipCode ?? ""),
    city: raw.city,
    state: raw.state,
    country: "BRA",
});

export const buildShareholder = (
    label: string,
    kind: ShareholderKind,
    values: Record<string, any>,
): BankingShareholder => {
    const base = {
        document: cleanDocument(values?.[`ownerDocument-${label}`]),
        type: values?.[`ownerType-${label}`],
        email: values?.[`ownerEmail-${label}`],
        phoneNumber: toBrazilianPhone(values?.[`ownerPhone-${label}`]),
        address: buildAddress({
            street: values?.[`ownerStreet-${label}`],
            number: values?.[`ownerNumber-${label}`],
            complement: values?.[`ownerComplement-${label}`],
            neighborhood: values?.[`ownerDistrict-${label}`] ?? "",
            zipCode: values?.[`ownerZipCode-${label}`],
            city: values?.[`ownerCity-${label}`],
            state: values?.[`ownerState-${label}`],
        }),
    };
    if (kind === ShareholderKind.NATURAL) {
        const { firstName, lastName } = splitFullName(values?.[`ownerFullName-${label}`]);
        return {
            ...base,
            firstName,
            lastName,
            birthDate: formatTextDate(values?.[`ownerBirthDate-${label}`]),
            monthlyRevenueRange: values?.[`ownerMonthlyRevenue-${label}`],
        };
    }
    return {
        ...base,
        legalName: values?.[`ownerLegalName-${label}`],
        annualRevenueRange: values?.[`ownerAnnualRevenue-${label}`],
    };
};
